import { CURRENT_STATE_PROJECT_KEY, getTypeName, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE } from "../../common/linqset-model"
import { getColourForSuperType, getColourForType, MODEL_COLOURS } from "../../model-services/model-item-enums"

export enum OUTPUT_SETTING {
    NONE,
    VALUE,
    NOVALUE
}

export class NodeStyleSettings {
    superType:NODE_SUPERTYPE = NODE_SUPERTYPE.ACTION
    type:NODE_TYPE = NODE_TYPE.ACTION
    external:boolean=false
    output:OUTPUT_SETTING=OUTPUT_SETTING.NONE
    excluded:boolean=false
    noText:boolean=false
    error:boolean=false
    projectChanged:boolean = false
    projectNew:boolean=false
    waiting:boolean=false       // an action where items wait, as the user said or LINQ read it (see WORK_TYPE)

    constructor(node?:LinqsetNode, isOutput:OUTPUT_SETTING=OUTPUT_SETTING.NONE) {
        if (!node) return

        this.superType = node.superType
        this.type = node.type
        this.external = node.external
        this.excluded = node.excluded
        this.output = isOutput
        this.projectNew = (node.projectKey !== CURRENT_STATE_PROJECT_KEY)
        this.projectChanged = !this.projectNew && node.properties?.projectKey && node.properties?.projectKey!==CURRENT_STATE_PROJECT_KEY
    }
}

export const createNodeSvg = (settings:NodeStyleSettings, x:number, y:number, width:number, height:number): SVGSVGElement => {
    const svg = createSvg(x, y, width, height)

    let colour:string = getColour(settings)

    if (settings.noText) {
        if (settings.type === NODE_TYPE.CAPTURE)
            addPolygons(svg, `0,7 0, ${width} ${height}, ${width} ${height}, 7 ${height/2},0`, colour)
        else if (settings.type === NODE_TYPE.OUTCOMES)
            addRectangles(svg, 0, 0, 15, 15, width, height, colour)
        else addRectangles(svg, 0, 0, 5, 5, width, height, colour)
        return svg
    }

    switch (settings.type) {
        case NODE_TYPE.SERVICE:
            addPolygons(svg, "35,17.5 28,35 0,35 0,0 28,0", colour)
            addText(svg, 'Service', settings)
            break;
        case NODE_TYPE.DATABASE:
            addDatabase(svg, colour)
            addText(svg, 'DB', settings, 17.5, 22)
            break
        case NODE_TYPE.FILE:
            addPolygons(svg, "32,0 32,28 24,35 3,35 3,0", colour)
            addPolyline(svg, "24,35 24,28 32,28")
            addText(svg, 'File', settings)
            break;
        case NODE_TYPE.PAPER:
            addPath(svg, "M31.6,0v27c-7.7,0-13.4,1.5-14.1,3.5S11.2,34,3.4,34V0H31.6z", colour)
            addText(svg, 'Paper', settings)
            break;
        case NODE_TYPE.VERBAL:
            addPath(svg, "m1.00084,4.61738l0,0c0,-2.15498 1.69292,-3.90193 3.78124,-3.90193l1.71875,0l0,0l8.24999,0l15.46873,0c1.00285,0 1.96462,0.41109 2.67375,1.14285c0.70912,0.73175 1.1075,1.72423 1.1075,2.75908l0,9.75483l0,0l0,5.85289l0,0c0,2.15498 -1.69292,3.90193 -3.78125,3.90193l-15.46873,0l-10.77759,9.9217l2.5276,-9.9217l-1.71875,0c-2.08832,0 -3.78124,-1.74695 -3.78124,-3.90193l0,0l0,-5.85289l0,0l0,-9.75483z", colour)
            addText(svg, 'Verbal', settings)
            break;
        case NODE_TYPE.CAPTURE:
            addPolygons(svg, "0,7 0,35 35,35 35,7 17.5,0", colour)
            addText(svg, 'Capture', settings)
            break;
        case NODE_TYPE.EXTERNAL_ORGANISATION:
            addPolygons(svg, "0,7 0,35 28,35 35,28 35,0 7,0", colour)
            addPolyline(svg, "0,7 28,7 35,0")
            svg.appendChild(createLine(28, 7, 28, 35, "none", "#FFFFFF", 1))
            addText(svg, "Ext", settings, 14, 22.5)

            break
        case NODE_TYPE.COMMENT:
            addComment(svg, colour)
            break
        case NODE_TYPE.TODO:
            addTodoList(svg, colour)
            break
        case NODE_TYPE.PERSON:
            svg.appendChild(createElipse(6, 6.5, 17.5, 11, "none", colour, 3, 4))
            svg.appendChild(createPath("m29.8,24.5l0,6.4l-24.5,0l0,-6.4c0,-3.8 3.7,-6.4 7.6,-6.4l10.5,0c3.8,0 6.4,2.6 6.4,6.4z", "none", colour, 3))
            break
        case NODE_TYPE.TEAM:
            svg.appendChild(createPath("m16.3,15.8l11.9,0c3.2,0 5,2.1 5,5.3l0,5.3l-31.4,0l0,-5.4c0,-3.1 3,-5.3 6.2,-5.3l8.3,0", "none", colour, 3))
            svg.appendChild(createElipse(4.8, 5.3, 11.8, 9.9, "none", colour, 3, 4))
            svg.appendChild(createElipse(4.8, 5.3, 23.5, 9.9, "none", colour, 3, 4))
            svg.appendChild(createPath("m27.4,25.7l0,5.3l-19.8,0l0,-5.3c0,-3.1 3.5,-5.3 6.7,-5.3l8.6,0c3.1,0 4.5,2.1 4.5,5.3z", "none", colour, 3))
            svg.appendChild(createElipse(4.8, 5.3, 19.1, 14.6, "none", colour, 3, 4))
            break
        case NODE_TYPE.OUTCOMES:
            addRectangles(svg, 0, 0, 10, 10, width, height, colour)
            addText(svg, "Outcome", settings)
            break
        case NODE_TYPE.INFRASTRUCTURE:
            addPath(svg, "m29.58731,18.55718l4.16861,-2.2018c-0.08195,-0.81579 -0.24032,-1.60716 -0.43845,-2.38511l-4.63073,-0.64038c-0.40081,-0.96423 -0.93008,-1.85852 -1.55224,-2.67944l1.76517,-4.33546c-0.58042,-0.5653 -1.20725,-1.08069 -1.867,-1.55492l-3.93209,2.4652c-0.92208,-0.48425 -1.91553,-0.85205 -2.95842,-1.08826l-1.421,-4.39635c-0.40301,-0.03039 -0.8059,-0.0625 -1.21689,-0.0625s-0.81274,0.03137 -1.21696,0.0625l-1.40692,4.3574c-1.07084,0.22865 -2.08623,0.60071 -3.03201,1.09094l-3.87086,-2.42894c-0.65892,0.47423 -1.28572,0.98962 -1.8661,1.55492l1.7166,4.2157c-0.66905,0.85632 -1.22919,1.80115 -1.64991,2.81945l-4.48454,0.61938c-0.19729,0.77686 -0.35649,1.56739 -0.4376,2.38502l4.02499,2.1267c0.0456,1.1195 0.23146,2.20448 0.56057,3.2262l-3.03623,3.35264c0.35319,0.73158 0.74642,1.44106 1.20132,2.10669l4.47017,-0.95837c0.73292,0.79638 1.56795,1.49414 2.48925,2.07116l-0.16944,4.56751c0.73415,0.33067 1.49695,0.60741 2.28303,0.82861l2.82802,-3.63026c0.50178,0.06397 1.0099,0.10706 1.52816,0.10706c0.56144,0 1.1105,-0.05065 1.65072,-0.12477l2.84275,3.6504c0.78732,-0.22095 1.54895,-0.49768 2.28303,-0.82836l-0.17271,-4.64954c0.87811,-0.56773 1.67436,-1.24694 2.37635,-2.01378l4.58643,0.9828c0.4549,-0.66553 0.84818,-1.37425 1.20125,-2.10644l-3.13458,-3.46241c0.30111,-0.96777 0.46931,-1.98938 0.51828,-3.04319l0,0zm-6.92854,5.28892l-1.97641,1.28491l-1.21562,-1.86521c-0.63239,0.24292 -1.31322,0.39062 -2.03093,0.39062c-3.13456,0 -5.67483,-2.53786 -5.67483,-5.66772c0,-3.12999 2.54027,-5.66687 5.67483,-5.66687c3.13374,0 5.67494,2.53687 5.67494,5.66687c0,1.56409 -0.63446,2.97801 -1.66003,4.00388l1.20805,1.85352z", colour)
            break
        case NODE_TYPE.CLOUD_BASED:
            addPath(svg, "m7.84655,30.41487c-3.96201,0 -7.17539,-3.70208 -7.17539,-8.26666l0,0c0.00239,-3.66351 2.07369,-6.74972 4.93547,-7.83128l0,0c0.09926,-3.11377 2.31646,-5.59928 5.04191,-5.60204l0,0c0.87301,0 1.6922,0.26178 2.40376,0.71231l0,0c1.51401,-2.90022 4.26936,-4.84426 7.42414,-4.84564l0,0c4.72021,0.00276 8.55905,4.34688 8.66787,9.75328l0,0c2.82471,1.10773 4.85774,4.18017 4.86013,7.81337l0,0c0,4.56457 -3.21338,8.26666 -7.17539,8.26666l0,0l-18.9825,0l0,0zm19.85311,-13.67443c-0.6111,-0.13089 -1.03326,-0.77431 -0.97466,-1.48662l0,0c0.02033,-0.2356 0.03229,-0.46293 0.03229,-0.68475l0,0c-0.00837,-3.99417 -2.81275,-7.22643 -6.27966,-7.23195l0,0c-2.68958,-0.00276 -4.97733,1.95369 -5.87306,4.69822l0,0c-0.1459,0.44502 -0.48553,0.77707 -0.89453,0.868l0,0c-0.40661,0.09093 -0.82995,-0.07164 -1.1086,-0.42573l0,0c-0.49749,-0.62413 -1.17916,-1.00715 -1.9529,-1.00715l0,0c-1.46498,0.00276 -2.65011,1.36813 -2.6549,3.05591l0,0c0,0.18738 0.02033,0.38027 0.05382,0.58418l0,0c0.0586,0.36649 -0.00957,0.744 -0.19493,1.04987l0,0c-0.18536,0.30724 -0.46879,0.51115 -0.78571,0.5704l0,0c-2.27221,0.43124 -4.00865,2.68529 -4.00626,5.41604l0,0c0.00478,3.04351 2.14305,5.50421 4.78479,5.51248l0,0l18.9837,0c2.64174,-0.00827 4.77642,-2.46897 4.7824,-5.51248l0,0c0.00478,-2.69631 -1.68622,-4.93244 -3.91178,-5.40639l0,0", colour)
            addPath(svg, "m22.28567,19.69922l1.58193,-0.68806c-0.0311,-0.25493 -0.0912,-0.50224 -0.16639,-0.74535l-1.7573,-0.20012c-0.1521,-0.30132 -0.35295,-0.58079 -0.58906,-0.83732l0.66986,-1.35483c-0.22026,-0.17666 -0.45814,-0.33771 -0.7085,-0.48591l-1.49218,0.77038c-0.34992,-0.15133 -0.72692,-0.26626 -1.12268,-0.34008l-0.53925,-1.37386c-0.15294,-0.0095 -0.30583,-0.01953 -0.46179,-0.01953s-0.30843,0.0098 -0.46182,0.01953l-0.53391,1.36169c-0.40637,0.07145 -0.7917,0.18772 -1.15061,0.34092l-1.46894,-0.75904c-0.25005,0.1482 -0.48791,0.30926 -0.70816,0.48591l0.65143,1.31741c-0.25389,0.2676 -0.46646,0.56286 -0.62612,0.88108l-1.70183,0.19356c-0.07487,0.24277 -0.13528,0.48981 -0.16606,0.74532l1.52743,0.66459c0.0173,0.34984 0.08783,0.6889 0.21273,1.00819l-1.15221,1.0477c0.13403,0.22862 0.28326,0.45033 0.45588,0.65834l1.69637,-0.29949c0.27813,0.24887 0.59502,0.46692 0.94464,0.64724l-0.0643,1.42735c0.2786,0.10333 0.56807,0.18982 0.86638,0.25894l1.0732,-1.13446c0.19042,0.01999 0.38324,0.03346 0.57992,0.03346c0.21306,0 0.42142,-0.01583 0.62643,-0.03899l1.07879,1.14075c0.29878,-0.06905 0.58781,-0.15552 0.86638,-0.25886l-0.06554,-1.45298c0.33323,-0.17742 0.6354,-0.38967 0.90179,-0.62931l1.74049,0.30712c0.17263,-0.20798 0.32187,-0.42945 0.45586,-0.65826l-1.18953,-1.082c0.11427,-0.30243 0.1781,-0.62168 0.19668,-0.951l0,0zm-2.62929,1.65279l-0.75002,0.40154l-0.46131,-0.58288c-0.23998,0.07591 -0.49835,0.12207 -0.77071,0.12207c-1.18953,0 -2.15353,-0.79308 -2.15353,-1.77116c0,-0.97812 0.964,-1.7709 2.15353,-1.7709c1.18922,0 2.15357,0.79277 2.15357,1.7709c0,0.48878 -0.24077,0.93063 -0.62996,1.25121l0.45844,0.57922z", colour)
            break
        case NODE_TYPE.ON_PREMISE:
            addOnPremise(svg, colour)
            break
        case NODE_TYPE.ASSET:
            addInformationAsset(svg, width, colour)
            break
        case NODE_TYPE.MODEL:
            addModel(svg, colour)
            break
        default:
            addRectangles(svg, 0, 0, 5, 5, width, height, colour)
            if (settings.waiting) {
                // nobody works on a step where items wait: a dashed outline, and it says so
                const outline = createRect(2.5, 2.5, width - 5, height - 5, 3, 3, "none", "#ffffff", 1.5)
                outline.setAttribute("stroke-dasharray", "4 3")
                svg.appendChild(outline)
                addText(svg, 'Wait', settings)
                break
            }
            const n = getTypeName(settings.type)
            if (n.length > 0) addText(svg, n, settings)
    }

    let outputPos = settings.type === NODE_TYPE.DATABASE?NODE_TYPE.DATABASE:(settings.type === NODE_TYPE.VERBAL?21:22)      // we adjust this depending on node

    if (settings.external) addExternalLine(svg, outputPos)
    if (settings.output) addOutputLine(settings, svg, 6)
    const addState = settings.type === NODE_TYPE.MODEL ? addModelProject : addProject
    if (settings.projectChanged) {
        addState(svg, MODEL_COLOURS.RED)
    }else if (settings.projectNew) {
        addState(svg, MODEL_COLOURS.GREEN)
    }

    return svg
}

const getColour = (settings:NodeStyleSettings) => {
    if (settings.excluded) return MODEL_COLOURS.EXCLUDED_COLOR

    if (settings.type) return getColourForType(settings.type)
    if (settings.superType) return getColourForSuperType(settings.superType)
    return MODEL_COLOURS.DARK_ORANGE
}

// each icon's gradient and shadow need their own ids: pages show many icons, and a reference to a shared id
// finds the first one in the page, which may be hidden or removed
let nextSvgId = 0

const shadowRef = (svg:SVGSVGElement) => `url(#${svg.dataset['shadow']})`
const gradientRef = (svg:SVGSVGElement) => `url(#${svg.dataset['gradient']})`

// Function to create the SVG element
const createSvg = (x:number, y:number, width:number, height:number): SVGSVGElement => {
    const n = ++nextSvgId
    const gradientId = `linq-depth-${n}`
    const shadowId = `linq-shadow-${n}`

    // Create the root SVG element
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.dataset['gradient'] = gradientId
    svg.dataset['shadow'] = shadowId
    // decorative: the name beside it, or the tooltip, says what it is
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('version', '1.1');
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute("viewBox", x + " " + y + " " + width + " " + height)

    svg.width.baseVal.newValueSpecifiedUnits(SVGLength.SVG_LENGTHTYPE_PX, width);
    svg.height.baseVal.newValueSpecifiedUnits(SVGLength.SVG_LENGTHTYPE_PX, height);

    svg.setAttribute('xml:space', 'preserve');

    // Create defs
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

    // Create linearGradient
    const linearGradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
    linearGradient.setAttribute('id', gradientId);
    linearGradient.setAttribute('x1', '0');
    linearGradient.setAttribute('y1', '0');
    linearGradient.setAttribute('x2', '0');
    linearGradient.setAttribute('y2', '1');

    // Create stops for linearGradient
    const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop1.setAttribute('offset', '0%');
    stop1.setAttribute('stop-color', '#ffffff');
    stop1.setAttribute('stop-opacity', '0.25');

    const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop2.setAttribute('offset', '100%');
    stop2.setAttribute('stop-color', '#000000');
    stop2.setAttribute('stop-opacity', '0.15');

    linearGradient.appendChild(stop1);
    linearGradient.appendChild(stop2);

    // Create filter
    const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
    filter.setAttribute('id', shadowId);
    filter.setAttribute('x', '-50%');
    filter.setAttribute('y', '-50%');
    filter.setAttribute('width', '200%');
    filter.setAttribute('height', '200%');

    // Create feOffset
    const feOffset = document.createElementNS('http://www.w3.org/2000/svg', 'feOffset');
    feOffset.setAttribute('dx', '0');
    feOffset.setAttribute('dy', '1');

    // Create feGaussianBlur
    const feGaussianBlur = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur');
    feGaussianBlur.setAttribute('stdDeviation', '1');
    feGaussianBlur.setAttribute('result', 'blur');

    // Create feComposite (inverse)
    const feCompositeInverse = document.createElementNS('http://www.w3.org/2000/svg', 'feComposite');
    feCompositeInverse.setAttribute('in', 'SourceGraphic');
    feCompositeInverse.setAttribute('in2', 'blur');
    feCompositeInverse.setAttribute('operator', 'out');
    feCompositeInverse.setAttribute('result', 'inverse');

    // Create feFlood
    const feFlood = document.createElementNS('http://www.w3.org/2000/svg', 'feFlood');
    feFlood.setAttribute('flood-color', '#000000');
    feFlood.setAttribute('flood-opacity', '0.25');

    // Create feComposite (in)
    const feCompositeIn = document.createElementNS('http://www.w3.org/2000/svg', 'feComposite');
    feCompositeIn.setAttribute('in2', 'inverse');
    feCompositeIn.setAttribute('operator', 'in');

    // Create feComposite (over)
    const feCompositeOver = document.createElementNS('http://www.w3.org/2000/svg', 'feComposite');
    feCompositeOver.setAttribute('in2', 'SourceGraphic');
    feCompositeOver.setAttribute('operator', 'over');

    filter.appendChild(feOffset);
    filter.appendChild(feGaussianBlur);
    filter.appendChild(feCompositeInverse);
    filter.appendChild(feFlood);
    filter.appendChild(feCompositeIn);
    filter.appendChild(feCompositeOver);

    defs.appendChild(linearGradient);
    defs.appendChild(filter);

    // Append all elements
    svg.appendChild(defs);

    return svg;
}

const createRect = (x:number, y:number, width:number, height:number, rx:number, ry:number, fill:string, stroke?:string, strokeWidth:number=1) => {
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.x.baseVal.value = x;
    rect.y.baseVal.value = y;
    rect.width.baseVal.value = width;
    rect.height.baseVal.value = height;
    rect.rx.baseVal.value = rx
    rect.ry.baseVal.value = ry
    if (stroke) {
        rect.setAttribute("stroke", stroke)
        rect.setAttribute("stroke-width", strokeWidth.toString())
    }
    rect.setAttribute('fill', fill);
    return rect
}

// Function to create the SVG element
const addRectangles = (svg:SVGSVGElement, x:number, y:number, rx:number, ry:number, width:number, height:number, colour:string) => {
    // Create first rect
    const rect1 = createRect(x, y, width, height, rx, ry, colour)
    rect1.setAttribute('filter', shadowRef(svg));

    // Create second rect
    const rect2 = createRect(x, y, width, height, rx, ry, gradientRef(svg))

    // Append all elements
    svg.appendChild(rect1);
    svg.appendChild(rect2);
}

const addText = (svg:SVGSVGElement, textToAdd:string, settings:NodeStyleSettings, x:number = 17.5, y:number=19) => {
    if (settings.noText) return

    let size = 12
    if (textToAdd.length === 1) size = 14
    else if (textToAdd.length <= 4) size = 11
    else if (textToAdd.length <= 6) size = 10
    else if (textToAdd.length >= 7) size = 8

    const colour = settings.error?MODEL_COLOURS.RED:'#ffffff'

    // Create text
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('fill', colour);
    text.setAttribute('font-family', 'Segoe UI, Arial, sans-serif');
    text.setAttribute('font-size', size.toString());
    text.setAttribute('font-weight', 'normal');
    text.setAttribute('x', x.toString());
    text.setAttribute('y', y.toString());
    text.textContent = textToAdd;
    svg.appendChild(text);
}

const addOutputLine = (settings:NodeStyleSettings, svg:SVGSVGElement, y:number) => {
    const c = settings.output == OUTPUT_SETTING.NOVALUE?MODEL_COLOURS.RED:'#ffffff'
    const rect = createRect(7, y, 21, 2, 1, 1, c)
    svg.appendChild(rect);
}

const addExternalLine = (svg:SVGSVGElement, y:number) => {
    const rect = createRect(7, y, 21, 2, 1, 1, '#ffffff')
    svg.appendChild(rect);
}

const addProject = (svg:SVGSVGElement, color:string) => {
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.cx.baseVal.value = 27.5;
    circle.cy.baseVal.value = 10;
    circle.r.baseVal.value = 4;
    circle.setAttribute('fill', color);
    svg.appendChild(circle);
}

// a project's new or changed model: the badge sits on the model's corner, ringed in white to stand off the sheet behind
const addModelProject = (svg:SVGSVGElement, color:string) => {
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.cx.baseVal.value = 29;
    circle.cy.baseVal.value = 6;
    circle.r.baseVal.value = 5;
    circle.setAttribute('fill', color);
    circle.setAttribute('stroke', '#FFFFFF');
    circle.setAttribute('stroke-width', '1.5');
    svg.appendChild(circle);
}

// a model: a process (action, information, action) on a stack of sheets, as a model holds many of them
const addModel = (svg:SVGSVGElement, colour:string) => {
    const back = createRect(6, 1, 28, 26, 4, 4, colour)
    back.setAttribute('opacity', '0.45')
    svg.appendChild(back)

    const front = createRect(1, 8, 28, 26, 4, 4, colour)
    front.setAttribute('filter', shadowRef(svg))
    svg.appendChild(front)
    svg.appendChild(createRect(1, 8, 28, 26, 4, 4, gradientRef(svg)))

    svg.appendChild(createLine(9, 21, 12, 21, "none", "#FFFFFF", 1.6))
    svg.appendChild(createLine(18, 21, 21, 21, "none", "#FFFFFF", 1.6))
    svg.appendChild(createRect(4, 18.5, 5, 5, 1, 1, "#FFFFFF"))
    svg.appendChild(createElipse(3, 3, 15, 21, "#FFFFFF", "none", 0, 0))
    svg.appendChild(createRect(21, 18.5, 5, 5, 1, 1, "#FFFFFF"))
}

const addDatabase = (svg:SVGSVGElement, colour:string) => {
    svg.appendChild(createRect(2, 8, 31, 23, 0, 0, colour))
    svg.appendChild(createElipse(16, 6, 17.5, 6, colour, '#FFFFFF', 1, 35))
    svg.appendChild(createElipse(16, 6, 17.5, 29, colour, colour, 1, 28))

    return svg;
}

const createPolygon = (points:string, fill:string, stroke?:string, strokeWidth:number=1) => {
    const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    polygon.setAttribute('points', points);
    polygon.setAttribute('fill', fill);
    if (stroke) {
        polygon.setAttribute('stroke', stroke)
        polygon.setAttribute('stroke-width', strokeWidth.toString())
    }

    return polygon
}

const addPolygons = (svg:SVGSVGElement, points:string, colour:string) => {
    const polygon1 = createPolygon(points, colour)
    polygon1.setAttribute('filter', shadowRef(svg));

    const polygon2 = createPolygon(points, gradientRef(svg))

    svg.appendChild(polygon1);
    svg.appendChild(polygon2);
}

const addPolyline = (svg:SVGSVGElement, points:string) => {
    const polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    polyline.setAttribute('points', points);
    polyline.setAttribute('stroke', '#ffffff');
    polyline.setAttribute('fill', 'none');
    polyline.setAttribute('stroke-width', '1');
    svg.appendChild(polyline)
}

const addPath = (svg:SVGSVGElement, d:string, colour:string) => {
    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', d);
    path1.setAttribute('fill', colour);
    path1.setAttribute('filter', shadowRef(svg));

    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', d);
    path2.setAttribute('fill', gradientRef(svg));

    svg.appendChild(path1);
    svg.appendChild(path2);
}

const createPath = (pathString:string, fill:string, stroke?:string, strokeWidth:number=0) => {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', pathString);
    path.setAttribute('fill', fill);
    if (stroke) {
        path.setAttribute('stroke', stroke)
        path.setAttribute('stroke-width', strokeWidth.toString())
    }

    path.setAttribute('clip-rule', 'evenodd')
    path.setAttribute('fill-rule', 'evenodd')
    return path
}

const addComment = (svg:SVGSVGElement, colour:string) => {
    const g1 = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    const g2 = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    const g3 = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    g1.appendChild(g2)
    g1.appendChild(g3)

    g2.appendChild(createPath("m27.47396,7.52083l-25.27604,0c-0.60717,0 -1.09896,0.46148 -1.09896,1.03125l0,15.46875c0,0.56977 0.49179,1.03125 1.09896,1.03125l4.39583,0c0.60717,0 1.09896,0.46148 1.09896,1.03125c0,0 0,5.67187 0,6.1875c0,0.71723 1.02148,1.61958 2.19792,0.51562c1.09896,-1.03125 7.69271,-7.21875 7.69271,-7.21875s0.38958,-0.51562 1.45942,-0.51562c0.361,0 8.43121,0 8.43121,0c0.60717,0 1.09896,-0.46148 1.09896,-1.03125l0,-15.46875c0,-0.56977 -0.49179,-1.03125 -1.09896,-1.03125z", colour))
    g2.appendChild(createPath("m32.96875,2.36458l-24.17708,0c-0.60717,0 -1.09896,0.46148 -1.09896,1.03125l0,3.09375l19.78125,0c1.2149,0 2.19792,0.92246 2.19792,2.0625l0,10.3125l3.29688,0c0.60717,0 1.09896,-0.46148 1.09896,-1.03125l0,-14.4375c0,-0.56977 -0.49179,-1.03125 -1.09896,-1.03125z",  colour))

    g3.appendChild(createPath("m32.96875,1.33333l-24.17708,0c-1.2149,0 -2.19792,0.92246 -2.19792,2.0625l0,3.09375l-4.39583,0c-1.2149,0 -2.19792,0.92246 -2.19792,2.0625l0,15.46875c0,1.14004 0.98302,2.0625 2.19792,2.0625l3.84635,0c0.30386,0 0.54948,0.23048 0.54948,0.51562l0,5.67187c0,0.83377 0.53464,1.5871 1.35667,1.90523c0.27254,0.10673 0.55772,0.15727 0.84125,0.15727c0.57091,0 1.13302,-0.20934 1.55393,-0.60431l7.77843,-7.29919c0,0 0.28354,-0.3465 0.91873,-0.3465s8.43121,0 8.43121,0c1.2149,0 2.19792,-0.92246 2.19792,-2.0625l0,-4.125l3.29688,0c1.2149,0 2.19792,-0.92246 2.19792,-2.0625l0,-14.4375c0,-1.14004 -0.98302,-2.0625 -2.19792,-2.0625l-0.00001,0.00001zm-4.39583,22.6875c0,0.56977 -0.49179,1.03125 -1.09896,1.03125c0,0 -8.0702,0 -8.43121,0c-1.06983,0 -1.45942,0.51562 -1.45942,0.51562s-6.59375,6.1875 -7.69271,7.21875c-1.17644,1.10396 -2.19792,0.20161 -2.19792,-0.51562c0,-0.51562 0,-6.1875 0,-6.1875c0,-0.56977 -0.49179,-1.03125 -1.09896,-1.03125l-4.39583,0c-0.60717,0 -1.09896,-0.46148 -1.09896,-1.03125l0,-15.46875c0,-0.56977 0.49179,-1.03125 1.09896,-1.03125l25.27604,0c0.60717,0 1.09896,0.46148 1.09896,1.03125l0,15.46875zm5.49479,-6.1875c0,0.56977 -0.49179,1.03125 -1.09896,1.03125l-3.29688,0l0,-10.3125c0,-1.14004 -0.98302,-2.0625 -2.19792,-2.0625l-19.78125,0l0,-3.09375c0,-0.56977 0.49179,-1.03125 1.09896,-1.03125l24.17708,0c0.60717,0 1.09896,0.46148 1.09896,1.03125l0,14.4375z", '#394240'))
    g3.appendChild(createPath("m7.14323,13.70833l7.14323,0c0.30386,0 0.54948,-0.23048 0.54948,-0.51562s-0.24562,-0.51562 -0.54948,-0.51562l-7.14323,0c-0.30386,0 -0.54948,0.23048 -0.54948,0.51562s0.24562,0.51562 0.54948,0.51562z", "#394240"))
    g3.appendChild(createPath("m22.52865,15.77083l-15.38542,0c-0.30386,0 -0.54948,0.23048 -0.54948,0.51562s0.24562,0.51562 0.54948,0.51562l15.38542,0c0.30386,0 0.54948,-0.23048 0.54948,-0.51562s-0.24562,-0.51562 -0.54948,-0.51562z", "#394240"))
    g3.appendChild(createPath("m18.68229,18.86458l-11.53906,0c-0.30386,0 -0.54948,0.23048 -0.54948,0.51562s0.24562,0.51562 0.54948,0.51562l11.53906,0c0.30386,0 0.54948,-0.23048 0.54948,-0.51562s-0.24562,-0.51562 -0.54948,-0.51562z", "#394240"))

    const p = createPath("m34.06771,17.83333c0,0.56977 -0.49179,1.03125 -1.09896,1.03125l-3.29688,0l0,-10.3125c0,-1.14004 -0.98302,-2.0625 -2.19792,-2.0625l-19.78125,0l0,-3.09375c0,-0.56977 0.49179,-1.03125 1.09896,-1.03125l24.17708,0c0.60717,0 1.09896,0.46148 1.09896,1.03125l0,14.4375z", "#231F20")
    p.setAttribute("opacity", "0.15")
    g1.appendChild(p)

    svg.appendChild(g1)
}

const addTodoList = (svg:SVGSVGElement, colour:string) => {
    svg.appendChild(createRect(5, 5, 26, 29, 1, 1, colour))
    const g1 = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g1.appendChild(createPath("m32.06932,3.92592l-8.31744,0c-0.42433,-2.21434 -3.07815,-3.92592 -6.28038,-3.92592s-5.85621,1.71158 -6.28038,3.92592l-8.48332,0c-1.6464,0 -3.04115,0.96066 -3.04115,2.14142l0,27.12458c0,1.18075 1.39476,2.14142 3.04115,2.14142l29.3615,0c1.6464,0 2.93068,-0.96066 2.93068,-2.14142l0,-27.12458c0,-1.18075 -1.28428,-2.14142 -2.93068,-2.14142l0.00001,0zm-23.93393,1.78451c0.56931,2.14534 3.16574,3.68799 6.31886,3.68799l6.0347,0c3.15313,0 5.74954,-1.54265 6.31886,-3.68799l2.8839,0l0,25.69697l-24.55086,0l0,-25.69697l2.99454,0zm9.33613,-3.80696c1.73448,0 3.19194,0.86358 3.58326,2.02244l-7.16637,0c0.39115,-1.15886 1.84861,-2.02244 3.5831,-2.02244z", '#000000'))
    g1.appendChild(createRect(9.12207, 12.96745, 8.29421, 1.78451, 0, 0, '#000000'))
    g1.appendChild(createPolygon("25.99066727158356,12.50062370300293 24.158640248811935,11.292868614196777 21.800096482076924,13.132817268371582 20.773276272108205,12.532865524291992 19.201684892187814,13.916337013244629 22.04560393140696,15.578073501586914 ", '#000000'))
    g1.appendChild(createRect(9.12207, 18.67789, 8.29421, 1.78451, 0, 0, '#000000'))
    g1.appendChild(createPolygon("25.99066727158356,18.2110595703125 24.158640248811935,17.00330352783203 21.800096482076924,18.8432559967041 20.773276272108205,18.243301391601562 19.201684892187814,19.62677764892578 22.04560393140696,21.28851318359375 ", '#000000'))
    g1.appendChild(createRect(9.12207, 24.38833, 8.29421, 1.78451, 0, 0, '#000000'))
    g1.appendChild(createPolygon("25.99066727158356,23.9215030670166 24.158640248811935,22.713743209838867 21.800096482076924,24.553693771362305 20.773276272108205,23.9537410736084 19.201684892187814,25.337215423583984 22.04560393140696,26.998950958251953 ", '#000000'))
    svg.appendChild(g1)
}

const createElipse = (rx:number, ry:number, cx:number, cy:number, fill:string, stroke:string, strokeWidth:number, width:number) => {
    const e = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');

    e.rx.baseVal.value = rx
    e.ry.baseVal.value = ry
    e.cx.baseVal.value = cx
    e.cy.baseVal.value = cy
    e.setAttribute('fill', fill)
    e.setAttribute('stroke', stroke)
    e.setAttribute('stroke-width', strokeWidth.toString())
    return e
}

const createLine = (x1:number, y1:number, x2:number, y2:number, fill:string, stroke:string, strokeWidth:number):SVGLineElement => {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');

    line.x1.baseVal.value = x1;
    line.y1.baseVal.value = y1;
    line.x2.baseVal.value = x2;
    line.y2.baseVal.value = y2;
    line.setAttribute('fill', fill);
    line.setAttribute('stroke', stroke);
    line.setAttribute('stroke-width', strokeWidth.toString())
    return line
}

const addOnPremise= (svg:SVGSVGElement, colour:string) => {
    const g1 = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    const g2 = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    g1.appendChild(createLine(15.97668, 22.20117, 18.85668, 22.20117, "none", colour, 2))
    g1.appendChild(createLine(15.97668, 16.26629, 18.85668, 16.26629, "none", colour, 2))
    g1.appendChild(createLine(15.97668, 10.34687, 18.85668, 10.34687, "none", colour, 2))
    g1.appendChild(createLine(10.23167, 22.20117, 13.11167, 22.20117, "none", colour, 2))
    g1.appendChild(createLine(10.23167, 16.26629, 13.11167, 16.26629, "none", colour, 2))
    g1.appendChild(createLine(10.23167, 10.34687, 13.11167, 10.34687, "none", colour, 2))
    g1.appendChild(createLine(21.73668, 22.20117, 24.60169, 22.20117, "none", colour, 2))
    g1.appendChild(createLine(21.73668, 16.26629, 24.60169, 16.26629, "none", colour, 2))
    g1.appendChild(createLine(21.73668, 10.34687, 24.60169, 10.34687, "none", colour, 2))

    g1.appendChild(createRect(13.11167, 28.12059, 8.62501, 5.91942, 0, 0, "none", colour, 2))

    g1.appendChild(createLine(34.66669, 34.04002, 0.16666, 34.04002, "none", colour, 2))
    g1.appendChild(createPolygon("24.6016845703125,4.427439829050115 24.6016845703125,1.4600000381469727 10.231669425964355,1.4600000381469727 10.231669425964355,4.427439829050115 5.911667346954346,4.427439829050115 5.911667346954346,34.040017071489615 28.92169952392578,34.040017071489615 28.92169952392578,4.427439829050115 24.6016845703125,4.427439829050115 ", "none", colour, 2))

    g2.appendChild(createPath("m1952.56402,-685.37771q0,106 -75,181q-75,75 -181,75q-106,0 -181,-75q-75,-75 -75,-181q0,-106 75,-181q75,-75 181,-75q106,0 181,75q75,75 75,181zm512,109l0,-222q0,-12 -8,-23q-8,-11 -20,-13l-185,-28q-19,-54 -39,-91q35,-50 107,-138q10,-12 10,-25q0,-13 -9,-23q-27,-37 -99,-108q-72,-71 -94,-71q-12,0 -26,9l-138,108q-44,-23 -91,-38q-16,-136 -29,-186q-7,-28 -36,-28l-222,0q-14,0 -24.5,8.5q-10.5,8.5 -11.5,21.5l-28,184q-49,16 -90,37l-141,-107q-10,-9 -25,-9q-14,0 -25,11q-126,114 -165,168q-7,10 -7,23q0,12 8,23q15,21 51,66.5q36,45.5 54,70.5q-27,50 -41,99l-183,27q-13,2 -21,12.5q-8,10.5 -8,23.5l0,222q0,12 8,23q8,11 19,13l186,28q14,46 39,92q-40,57 -107,138q-10,12 -10,24q0,10 9,23q26,36 98.5,107.5q72.5,71.5 94.5,71.5q13,0 26,-10l138,-107q44,23 91,38q16,136 29,186q7,28 36,28l222,0q14,0 24.5,-8.5q10.5,-8.5 11.5,-21.5l28,-184q49,-16 90,-37l142,107q9,9 24,9q13,0 25,-10q129,-119 165,-170q7,-8 7,-22q0,-12 -8,-23q-15,-21 -51,-66.5q-36,-45.5 -54,-70.5q26,-50 41,-98l183,-28q13,-2 21,-12.5q8,-10.5 8,-23.5z", colour))

    const matrix = svg.createSVGMatrix();
    matrix.a = 0.00964163; // Scale x
    matrix.d = -0.0095697 // Scale y
    matrix.e = 9.13398 // Translate x
    matrix.f = 12.7398 // Translate y

    // Create a new SVGTransform from the matrix
    const transform = svg.createSVGTransformFromMatrix(matrix);

    // Apply the transform to the rectangle
    g2.transform.baseVal.appendItem(transform);

    svg.appendChild(g1)
    svg.appendChild(g2)
}

const addInformationAsset = (svg:SVGSVGElement, width:number, colour:string) => {
    const white = "#FFFFFF"
    svg.appendChild(createElipse(16, 15.7, 17.58, 17.67, colour, colour, 1, width))
    svg.appendChild(createRect(14.5, 5.5, 6.5, 6, 0.5, 0, white, white, 1))
    svg.appendChild(createRect(14.5, 15, 6.5, 14.5, 0.5, 0, white, white, 1))

}